import { useEffect, useMemo, useRef } from 'react';
import { api } from './api';

export interface Breath {
  getSpectrum(out: Uint8Array): boolean;
  unlock(): void;
}

interface Graph {
  ctx: AudioContext;
  analyser: AnalyserNode;
  data: Uint8Array<ArrayBuffer>;
}

const graphs = new WeakMap<HTMLMediaElement, Graph | 'failed'>();

// Mobile browsers suspend the AudioContext when the page backgrounds, which would silence
// playback routed through it — on touch devices the <audio> stays bare and the waveform
// reads a spectrum the server precomputed from the cached file instead.
const NO_GRAPH =
  typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

function ensureGraph(el: HTMLMediaElement): Graph | null {
  if (NO_GRAPH) return null;
  const existing = graphs.get(el);
  if (existing) return existing === 'failed' ? null : existing;

  let src: MediaElementAudioSourceNode | undefined;
  let ctx: AudioContext | undefined;
  try {
    const Ctor: typeof AudioContext = window.AudioContext ?? (window as any).webkitAudioContext;
    ctx = new Ctor();
    src = ctx.createMediaElementSource(el);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    src.connect(analyser);
    analyser.connect(ctx.destination);
    const graph: Graph = { ctx, analyser, data: new Uint8Array(analyser.frequencyBinCount) };
    graphs.set(el, graph);
    return graph;
  } catch {
    // a graph from a prior module instance (StrictMode / HMR) may already be wired to `el` and
    // playing — never touch it; just make sure this attempt didn't leave audio routed nowhere
    if (src && ctx) {
      try {
        src.connect(ctx.destination);
      } catch {
        /* ignore */
      }
    }
    if (!graphs.get(el)) graphs.set(el, 'failed');
    return null;
  }
}

// callable outside a user gesture (e.g. mediaSession/autoplay) — building the graph is unrestricted,
// only ctx.resume() needs one, and a later real gesture will resume an already-built graph
export function primeBreath(el: HTMLMediaElement): void {
  const graph = ensureGraph(el);
  if (graph && graph.ctx.state !== 'running') {
    void graph.ctx.resume().catch(() => {});
  }
}

// --- precomputed spectrum (coarse-pointer only) ---------------------------------------------
interface Spec {
  fps: number;
  bands: number;
  frames: number;
  data: Uint8Array;
}

const specCache = new Map<string, Spec>();

async function fetchSpec(videoId: string): Promise<Spec | null> {
  const cached = specCache.get(videoId);
  if (cached) return cached;
  try {
    const r = await api.spectrum(videoId);
    const bin = atob(r.data);
    const data = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) data[i] = bin.charCodeAt(i);
    const spec: Spec = { fps: r.fps, bands: r.bands, frames: r.frames, data };
    specCache.set(videoId, spec);
    if (specCache.size > 12) {
      const oldest = specCache.keys().next().value;
      if (oldest) specCache.delete(oldest);
    }
    return spec;
  } catch {
    return null;
  }
}

// 首播时服务器可能还没缓存完这首歌 — 每10秒一轮, 缓存一落地波形就切成真实频谱
const RETRY_MS = [2000, 5000, 10000];

export function useBreath(
  el: HTMLAudioElement | null,
  playing: boolean,
  videoId?: string | null,
): Breath {
  const playingRef = useRef(playing);
  playingRef.current = playing;
  const specRef = useRef<Spec | null>(null);

  useEffect(() => {
    specRef.current = null;
    if (!NO_GRAPH || !videoId) return;
    let dead = false;
    let timer: number | undefined;
    let attempt = 0;
    const tryFetch = async () => {
      const spec = await fetchSpec(videoId);
      if (dead) return;
      if (spec) {
        specRef.current = spec;
        return;
      }
      timer = window.setTimeout(tryFetch, RETRY_MS[Math.min(attempt++, RETRY_MS.length - 1)]);
    };
    void tryFetch();
    return () => {
      dead = true;
      if (timer) clearTimeout(timer);
    };
  }, [videoId]);

  return useMemo<Breath>(() => {
    return {
      getSpectrum(out) {
        if (!playingRef.current) return false;
        if (NO_GRAPH) {
          const spec = specRef.current;
          if (!spec || !spec.frames || !el || el.paused) return false;
          const f = Math.max(0, Math.min(spec.frames - 1, Math.floor(el.currentTime * spec.fps)));
          const row = f * spec.bands;
          const n = out.length;
          for (let i = 0; i < n; i++) {
            const pos = (i * (spec.bands - 1)) / (n - 1);
            const a = Math.floor(pos);
            const b = Math.min(spec.bands - 1, a + 1);
            const t = pos - a;
            out[i] = spec.data[row + a] * (1 - t) + spec.data[row + b] * t;
          }
          return true;
        }
        if (!el) return false;
        const graph = graphs.get(el);
        if (!graph || graph === 'failed' || graph.ctx.state !== 'running') return false;
        try {
          graph.analyser.getByteFrequencyData(graph.data);
        } catch {
          return false;
        }
        const usable = 80; // lower ~5 kHz of the 128 bins — where music lives
        const n = out.length;
        for (let i = 0; i < n; i++) {
          const a = Math.floor((i * usable) / n);
          const b = Math.max(a + 1, Math.floor(((i + 1) * usable) / n));
          let s = 0;
          for (let j = a; j < b; j++) s += graph.data[j];
          out[i] = s / (b - a);
        }
        return true;
      },
      unlock() {
        if (!el || NO_GRAPH) return;
        primeBreath(el);
      },
    };
  }, [el]);
}
